/* eslint-disable max-len */
import React, { useEffect, useMemo, useState, useContext } from "react";
import { Container, useScreenClass } from "react-grid-system";
import styled, { css } from "styled-components";
import Navbar, { ProfileTag } from "../components/Navbar";
import BaseStyle from "../styles/BaseStyle";
import Sidebar from "../components/Sidebar";
import SidebarMenuItem from "../components/SidebarMenuItem";
import User, { Role } from "../types/User";
import theme from "../styles/theme";
import Icon, { ClickableIcon } from "../components/Icon";
import Backdrop from "../components/Backdrop";
import { UserContext } from "../context/UserContext";
import LogoImage from "../assets/logo.png";
import LogoNewImage from "../assets/logonew3.png";
import { RightAlign } from "../components/UtilsComponents";
import { FlexGrow } from "../components/FlexBox";
import { StyledText } from "../components/Text";
import MobileStyle from "../styles/MobileStyle";
import MinimalSelect from "../components/MinimalSelect";
import { convertRolesToSelect } from "../hooks/useScript";
import axios from "axios";
import { useNavigate } from "react-router-dom";

type PageAreaProps = {
  signedIn: boolean;
  isSidebarOpen?: boolean;
  shouldShift?: boolean;
  isBehalfActive?: boolean;
} & React.HTMLProps<HTMLDivElement>;
const PageArea = styled.div<PageAreaProps>`
  padding-top: ${({ signedIn, theme }) => (signedIn ? theme.navbarHeight : 0)};
  padding-left: ${({ signedIn, isSidebarOpen, theme }) =>
    signedIn && isSidebarOpen
      ? theme.sidebarWidth
      : signedIn
      ? `calc(${theme.sidebarWidth} - 180px)`
      : 0};
  transition: all ${({ theme }) => theme.transitionTime.default};
  ${({ isBehalfActive }) =>
    isBehalfActive &&
    css`
      padding-top: ${({ theme }) =>
        `calc(${theme.navbarHeight} + ${theme.spacing.x8})`};
    `}
`;

const StyledContainer = styled(Container)<{ $signedIn: boolean }>`
  padding-top: ${({ $signedIn, theme }) => ($signedIn ? theme.spacing.x2 : 0)};
  padding-left: ${({ $signedIn }) => !$signedIn && 0} !important;
  padding-right: ${({ $signedIn }) => !$signedIn && 0} !important;
  max-width: ${({ $signedIn }) => !$signedIn && "100% !important"};
  color: #fff;
  max-width: 100% !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
`;

const LogoDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

const Name = styled.img`
  position: absolute;
  left: 7%;
  top: 9px;
  width: 60px;
  text-decoration: none;
  padding: 0;
  margin: 0;
  align-content: center;
  display: flex;
  color: rgb(255, 255, 255);
  box-shadow: rgb(0 0 0 / 12%) 0px 6px 35px;
  border-radius: 4px;
}
`;
const Toogle = styled.div`
  position: absolute;
  right: 5%;
  left: auto;
  top: 25px;
  bottom: 92.89%;
  color: #ffffff;
`;
const ToogleCenter = styled.div`
  position: absolute;
  right: 35%;
  left: auto;
  top: 25px;
  bottom: 92.89%;
  color: #ffffff;
`;

export type Props = {
  children: React.ReactNode;
  user?: User;
};

function BasicLayout({ children }: Props): JSX.Element {
  const screenClass = useScreenClass();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMemo(
    () => ["xs", "sm", "md"].includes(screenClass),
    [screenClass]
  );
  const smallScreen = useMemo(
    () => ["xs", "sm"].includes(screenClass),
    [screenClass]
  );
  const shouldShowSidebarByDefault = useMemo(() => !isMobile, [isMobile]);
  const [isSidebarOpen, setSidebarOpen] = useState(shouldShowSidebarByDefault);
  const {
    isSignedIn,
    roadmapView,
    user,
    setSidebarView,
    setCurrentRoadmapView,
    setCurrentTimeView,
    setCurrentUser,
    setSearchView,
  } = useContext(UserContext);
  // useEffect(() => () => {
  //   // Reset Sidebar when close
  //   if (isCourseOpen) {
  //     setIsCourseOpen(false);
  //   }
  //   if (isDocumentOpen) {
  //     setIsDocumentOpen(false);
  //   }
  // }, [isSidebarOpen]);
  useEffect(() => {
    setSidebarOpen(shouldShowSidebarByDefault);
    setSidebarView(shouldShowSidebarByDefault);
  }, [shouldShowSidebarByDefault]);
  const SetSidebarClose = () => {
    setSearchView(false);
    if (isMobile) {
      setSidebarOpen(false);
      setSidebarView(false);
    }
  };

  const getStudentIdentity = (
    stuId: string,
    role: number,
    roles: Array<Role>
  ) => {
    setIsLoading(true);
    axios
      .get(`/student/identity?student_id=${stuId}`)
      .then((res) => {
        const roadmapView = localStorage.getItem("roadmapView");
        const initialValue = JSON.parse(roadmapView as string);
        if (initialValue) {
          if (
            initialValue?.value !== "" &&
            initialValue.url !== "/student-roadmaps"
          ) {
            setCurrentRoadmapView(initialValue);
          }
        }
        const timeView = localStorage.getItem("timeView");
        const initialTimeValue = JSON.parse(timeView as string);
        if (initialTimeValue) {
          if (initialTimeValue?.label !== "") {
            setCurrentTimeView(initialTimeValue);
          }
        }
        setCurrentUser({ role: role, roles: roles, ...res.data.student });
        setIsLoading(false);
        navigate("/roadmap/timeline");
      })
      .catch((err) => {
        setIsLoading(false);
      });
  };
  const getInstructorIdentity = (
    insId: string,
    role: number,
    roles: Array<Role>
  ) => {
    axios
      .get(`/instructor/identity?instructor_id=${insId}`)
      .then((res) => {
        const roadmapView = localStorage.getItem("roadmapView");
        const initialValue = JSON.parse(roadmapView as string);
        if (initialValue) {
          if (initialValue?.value !== "") setCurrentRoadmapView(initialValue);
        }
        const timeView = localStorage.getItem("timeView");
        const initialTimeValue = JSON.parse(timeView as string);
        if (initialTimeValue) {
          if (initialTimeValue?.label !== "")
            setCurrentTimeView(initialTimeValue);
        }
        setCurrentUser({
          role: role,
          roles: roles,
          ...res.data.instructor,
        });
        setIsLoading(false);
        navigate("/explore");
      })
      .catch((err) => {
        setIsLoading(false);
      });
  };

  const getAdminIdentity = (
    adminId: string,
    role: number,
    roles: Array<Role>
  ) => {
    axios
      .get(`/admin/identity?admin_id=${adminId}`)
      .then((res) => {
        const roadmapView = localStorage.getItem("roadmapView");
        const initialValue = JSON.parse(roadmapView as string);
        if (initialValue) {
          if (initialValue?.value !== "") setCurrentRoadmapView(initialValue);
        }
        const timeView = localStorage.getItem("timeView");
        const initialTimeValue = JSON.parse(timeView as string);
        if (initialTimeValue) {
          if (initialTimeValue?.label !== "")
            setCurrentTimeView(initialTimeValue);
        }
        setCurrentUser({
          role: role,
          roles: roles,
          ...res.data.admin,
        });
        setIsLoading(false);
        navigate("/explore");
      })
      .catch((err) => {
        setIsLoading(false);
      });
  };

  let curUser: JSX.Element = (
    <SidebarMenuItem
      isOpen={isSidebarOpen}
      name="Sign In"
      to="/signin"
      margin={1.5}
      icon="circle"
      isMain
      fontSize="16px"
      click={SetSidebarClose}
    />
  );
  if (isSignedIn) {
    curUser = (
      <>
        <LogoDiv>
          {isSidebarOpen ? (
            <>
              <Name src={LogoNewImage} alt="logo" />
              <Toogle>
                <ClickableIcon
                  onClick={() => {
                    setSidebarOpen(!isSidebarOpen);
                    setSidebarView(!isSidebarOpen);
                  }}
                  name="left-arrow-to-left"
                ></ClickableIcon>
              </Toogle>
            </>
          ) : (
            <ToogleCenter>
              <ClickableIcon
                onClick={() => {
                  setSidebarOpen(!isSidebarOpen);
                  setSidebarView(!isSidebarOpen);
                }}
                name="arrow-to-right"
              ></ClickableIcon>
            </ToogleCenter>
          )}
        </LogoDiv>
        {/* <SidebarMenuItem
          isOpen={isSidebarOpen}
          name="Dashboard"
          to="/"
          margin={1.5}
          icon="home"
          isMain
          fontSize="16px"
          click={SetSidebarClose}
        /> */}
        {user.role === 1 && (
          <>
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="My Roadmap"
              to={roadmapView.url}
              margin={1.5}
              icon="map"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="My Credits"
              to="/credits"
              margin={1.5}
              icon="layer-group"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            {/* <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="My Mastery"
              to="/profile"
              margin={1.5}
              icon="chart-pie"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            /> */}
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="My Portfolio"
              to="/portfolio"
              margin={1.5}
              icon="graduation-hat"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="My Cards"
              to="/mycompetencies"
              margin={1.5}
              icon="layer-group"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Explore Competencies"
              to="explore"
              margin={1.5}
              icon="grid"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Recommended Roadmaps"
              to="/recommended"
              margin={1.5}
              icon="compass"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Activity Calendar"
              to="/calendar"
              margin={1.5}
              icon="calender"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Announcements"
              to="/announcement"
              margin={1.5}
              icon="megaphone"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />

            {/* <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Help"
              to="/help"
              margin={1.5}
              icon="envelope-question"
              isMain
              fontSize="16px"
              click={SetSidebarClose}/> */}
          </>
        )}
        {[2, 3, 4, 5, 6].includes(user.role) && (
          <>
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Explore Competencies"
              to="explore"
              margin={1.5}
              icon="grid"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            {user.role !== 6 && (
              <SidebarMenuItem
                isOpen={isSidebarOpen}
                name="My Competencies"
                to="/instructor-competencies"
                margin={1.5}
                icon="layer-group"
                isMain
                fontSize="16px"
                click={SetSidebarClose}
              />
            )}
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Student Roadmaps"
              to="/student-roadmaps"
              margin={1.5}
              icon="compass"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Student Cards"
              to="/student-cards"
              margin={1.5}
              icon="layer-group"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Student Credits"
              to="/student-credits"
              margin={1.5}
              icon="layer-group"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            {/* <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Student Mastery"
              to="/student-profile"
              margin={1.5}
              icon="chart-pie"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            /> */}
            {/* <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Student Portfolio"
              to="/portfolio"
              margin={1.5}
              icon="graduation-hat"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            /> */}
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Activity Calendar"
              to="/calendar"
              margin={1.5}
              icon="calender"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
            <SidebarMenuItem
              isOpen={isSidebarOpen}
              name="Announcements"
              to="/announcement"
              margin={1.5}
              icon="megaphone"
              isMain
              fontSize="16px"
              click={SetSidebarClose}
            />
          </>
        )}
        {isSidebarOpen && (
          <FlexGrow
            style={{
              marginTop: "auto",
              flexGrow: "0",
              padding: "10px 15px",
              display: "flex",
              flexWrap: "nowrap",
            }}
          >
            <FlexGrow style={{ flexGrow: 0, alignSelf: "center" }}>
              <StyledText
                color="rgb(255, 255, 255)"
                style={{ marginRight: "8px" }}
              >
                Role
              </StyledText>
            </FlexGrow>
            <FlexGrow>
              <MinimalSelect
                setClass={isMobile ? "rs-container" : ""}
                menuPlacement="top"
                placeholder="Select Role"
                options={convertRolesToSelect(
                  (user.roles = user.roles?.filter(
                    (role: Role) => role.role !== 3
                  ))
                )}
                defaultValue={{
                  label:
                    user.role === 1
                      ? "Student"
                      : user.role === 2
                      ? "Instructor"
                      : "Admin",
                }}
                onChange={(data: any) => {
                  if (data.role && data.role === 1) {
                    localStorage.clear();
                    localStorage.setItem(
                      "role",
                      JSON.stringify({
                        role: data.role,
                        role_id: data.value,
                        roles: user.roles,
                      })
                    );
                    getStudentIdentity(data.value, data.role, user.roles);
                  } else if (data.role && data.role === 2) {
                    localStorage.clear();
                    localStorage.setItem(
                      "role",
                      JSON.stringify({
                        role: data.role,
                        role_id: data.value,
                        roles: user.roles,
                      })
                    );
                    getInstructorIdentity(data.value, data.role, user.roles);
                  } else if (data.role && data.role === 6) {
                    localStorage.clear();
                    localStorage.setItem(
                      "role",
                      JSON.stringify({
                        role: data.role,
                        role_id: data.value,
                        roles: user.roles,
                      })
                    );
                    getAdminIdentity(data.value, data.role, user.roles);
                  } else {
                    setIsLoading(false);
                    navigate("/unauthorized");
                  }
                  navigate("/explore");
                  window.location.reload();
                }}
              />
            </FlexGrow>
          </FlexGrow>
        )}
        {isSidebarOpen && (
          <FlexGrow
            style={{
              flexGrow: "0",
              padding: "10px 5px 10px 10px",
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            {/* <FlexGrow>
              <ProfileTag
                style={{
                  width: "40px",
                  height: "40px",
                  justifyContent: "center",
                  background:
                    "linear-gradient( 90deg, rgb(176,52,52) 0%, rgb(24,75,104) 118.29% )",
                  borderRadius: "50%",
                }}
                isMobile={isMobile}
              >
                <Icon color="white" fixedWidth={false} name="user"></Icon>
              </ProfileTag>
            </FlexGrow> */}
            <FlexGrow>
              {user.role === 1 && (
                <>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <ProfileTag
                      style={{
                        minWidth: "40px",
                        width: "40px",
                        height: "40px",
                        justifyContent: "center",
                        marginRight: "8px",
                        background:
                          "linear-gradient( 90deg, rgb(176,52,52) 0%, rgb(24,75,104) 118.29% )",
                        borderRadius: "50%",
                      }}
                      isMobile={isMobile}
                    >
                      <Icon color="white" fixedWidth={false} name="user"></Icon>
                    </ProfileTag>
                    <div>
                      <StyledText
                        color="rgb(255, 255, 255)"
                        style={{ marginRight: "8px" }}
                      >
                        {user.first_name}
                      </StyledText>
                      <StyledText color="rgb(255, 255, 255)">
                        {user.last_name}
                      </StyledText>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ minWidth: 48 }}></div>
                    <StyledText color="#E8EAED" fontWeight="bold">
                      {user?.programs?.[0]?.program_name || ""}
                    </StyledText>
                  </div>
                </>
              )}
              {[2, 3, 4, 5, 6].includes(user.role) && (
                <>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <ProfileTag
                      style={{
                        minWidth: "40px",
                        width: "40px",
                        height: "40px",
                        justifyContent: "center",
                        marginRight: "8px",
                        background:
                          "linear-gradient( 90deg, rgb(176,52,52) 0%, rgb(24,75,104) 118.29% )",
                        borderRadius: "50%",
                      }}
                      isMobile={isMobile}
                    >
                      <Icon color="white" fixedWidth={false} name="user"></Icon>
                    </ProfileTag>
                    <div>
                      <StyledText
                        color="rgb(255, 255, 255)"
                        style={{ marginRight: "8px" }}
                      >
                        {user.title}
                      </StyledText>
                    </div>
                    <div>
                      <StyledText color="rgb(255, 255, 255)">
                        {user.name}
                      </StyledText>
                    </div>
                  </div>
                  {/* <div>
                    <StyledText
                      color="rgb(255, 255, 255)"
                      style={{ marginRight: "8px" }}
                    >
                      {user.title}
                    </StyledText>
                  </div>
                  <div>
                    <StyledText color="rgb(255, 255, 255)">
                      {user.name}
                    </StyledText>
                  </div> */}
                </>
              )}
            </FlexGrow>
          </FlexGrow>
        )}
      </>
    );
  }
  return (
    <>
      {smallScreen ? <MobileStyle /> : <BaseStyle />}
      <Backdrop
        show={isSidebarOpen && isMobile}
        clicked={() => {
          setSidebarOpen(false);
          setSidebarView(false);
        }}
      />
      {isSignedIn && (
        <>
          <Navbar
            isOpen={isSidebarOpen}
            onSidebarToggle={() => {
              setSidebarOpen(!isSidebarOpen);
              setSidebarView(!isSidebarOpen);
            }}
            showSignout={!isSidebarOpen}
          />
          <Sidebar isOpen={isSidebarOpen}>{curUser}</Sidebar>
        </>
      )}
      <PageArea
        signedIn={isSignedIn}
        isSidebarOpen={isSidebarOpen}
        shouldShift={shouldShowSidebarByDefault}
      >
        <StyledContainer $signedIn={isSignedIn}>{children}</StyledContainer>
      </PageArea>
    </>
  );
}

export default BasicLayout;
